// hooks/useDeptOwnerMap.js — loads dept-owner mapping + O365 department list
import { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { getAccessToken } from "../services/msalService";
import { fetchDeptOwnerMap, saveDeptOwnerMap } from "../services/deptOwnerService";
import { fetchOrgDepartments } from "../services/graphUserService";

// Ensures every dept value is an array (handles old single-owner format)
function normalizeMap(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  Object.entries(raw).forEach(([dept, value]) => {
    if (!dept) return;
    out[dept] = Array.isArray(value) ? value : (value ? [value] : []);
  });
  return out;
}

export function useDeptOwnerMap() {
  const { instance, accounts } = useMsal();
  const [map, setMap]               = useState({});
  const [fileExists, setFileExists] = useState(null); // null = loading, true/false after load
  const [o365Depts, setO365Depts]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken(instance, accounts);
        const [raw, depts] = await Promise.all([
          fetchDeptOwnerMap(token),
          fetchOrgDepartments(instance, accounts),
        ]);
        if (cancelled) return;
        setFileExists(raw !== null);
        setMap(normalizeMap(raw));
        setO365Depts(depts);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [instance, accounts]);

  // Merge O365 depts with mapping keys so every mapped dept appears in the list
  const departments = [
    ...new Set([...o365Depts, ...Object.keys(map)]),
  ].sort();

  const saveMap = useCallback(async (newMap) => {
    const token = await getAccessToken(instance, accounts);
    await saveDeptOwnerMap(token, newMap);
    setMap(normalizeMap(newMap));
    setFileExists(true);
  }, [instance, accounts]);

  return { map, fileExists, departments, loading, error, saveMap };
}
